<template>
  <div class="ocr-report-column-map">
    <div class="ocr-report-map-toolbar">
      <v-btn size="small" color="primary" variant="flat" :loading="predicting" @click="runPredictColumns">
        Predict columns
      </v-btn>
      <v-btn size="small" variant="outlined" :loading="applying" @click="applyMappings">
        Apply column mapping
      </v-btn>
      <v-btn size="small" variant="tonal" :loading="savingConfig" @click="saveConfig">
        Save mapping config
      </v-btn>
    </div>
    <div v-if="loading && !mappings.length" class="ocr-empty-cell">Loading column mappings…</div>
    <div v-else-if="!mappings.length" class="ocr-empty-cell">
      Set a table type and save edited data, then predict columns.
    </div>
    <OcrTableWrap v-else title="Column mapping" class="ocr-report-column-map-table">
      <v-table class="well-table sample-table well-table-data fd-table">
        <thead>
          <tr class="main-header">
            <th>Report column</th>
            <th>Standard column</th>
            <th>Report UOM</th>
            <th>Standard UOM</th>
          </tr>
        </thead>
        <tbody class="well-table-content">
          <tr v-for="(row, idx) in mappings" :key="row.original_column + idx">
            <td>
              <input v-model="row.edited_column" class="ocr-editable-grid-input" type="text" />
            </td>
            <td>
              <v-select
                v-model="row.mapped_column"
                :items="standardColumnChoices"
                :menu-props="selectMenuProps"
                density="compact"
                variant="outlined"
                hide-details
                class="ocr-report-column-select"
                @update:model-value="onStandardColumn(row, $event)"
              />
            </td>
            <td>
              <input
                v-if="row.has_uom"
                v-model="row.original_uom"
                class="ocr-editable-grid-input ocr-report-uom-input"
                type="text"
                placeholder="Report unit"
              />
              <span v-else class="ocr-empty-cell">—</span>
            </td>
            <td>
              <v-select
                v-if="row.has_uom"
                v-model="row.mapped_uom"
                :items="uomItems(row)"
                :menu-props="selectMenuProps"
                density="compact"
                variant="outlined"
                hide-details
                class="ocr-report-column-select"
              />
              <span v-else class="ocr-empty-cell">—</span>
            </td>
          </tr>
        </tbody>
      </v-table>
    </OcrTableWrap>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";
import OcrTableWrap from "@/components/layout/OCR/OcrTableWrap.vue";

export default {
  name: "OcrReportColumnMap",
  components: { OcrTableWrap },
  props: {
    filename: { type: String, default: null },
    tableIndex: { type: Number, default: null },
    sourceGrid: { type: Object, default: null },
    tableType: { type: String, default: "" },
    listRevision: { type: Number, default: 0 },
  },
  emits: ["updated", "status"],
  data() {
    return {
      loading: false,
      predicting: false,
      applying: false,
      savingConfig: false,
      standardColumns: [],
      uomCatalog: {},
      mappings: [],
      loadToken: 0,
      selectMenuProps: { contentClass: "ocr-select-menu" },
      fallbackUom: ["Select UOM"],
    };
  },
  computed: {
    standardColumnChoices() {
      const items = [...(this.standardColumns || [])];
      const known = new Set(items);
      this.mappings.forEach((row) => {
        const val = row.mapped_column;
        if (val && val !== "No Mapping" && !known.has(val)) {
          items.push(val);
          known.add(val);
        }
      });
      return items;
    },
  },
  watch: {
    filename: { handler() { this.load(); } },
    tableIndex: { handler() { this.load(); } },
    tableType() { this.load(); },
    listRevision() { this.load(); },
  },
  mounted() {
    this.load();
  },
  methods: {
    fieldHasUom(field) {
      return Object.prototype.hasOwnProperty.call(this.uomCatalog || {}, field);
    },
    uomItems(row) {
      const fromCatalog = this.uomCatalog?.[row.mapped_column];
      if (Array.isArray(fromCatalog) && fromCatalog.length) return fromCatalog;
      return row.uom_options?.length ? row.uom_options : this.fallbackUom;
    },
    onStandardColumn(row, field) {
      const has = this.fieldHasUom(field);
      row.has_uom = has;
      row.uom_options = has ? (this.uomCatalog[field] || []) : [];
      row.mapped_uom = has ? "Select UOM" : "";
      if (!has) row.original_uom = "";
    },
    normalizeMappingRow(m) {
      const mapped = m.mapped_column || "No Mapping";
      const has = this.fieldHasUom(mapped) || !!m.has_uom;
      const options = this.uomCatalog?.[mapped] || m.uom_options || [];
      return {
        ...m,
        mapped_column: mapped,
        mapped_uom: has ? (m.mapped_uom || "Select UOM") : "",
        original_uom: has ? (m.original_uom || "") : "",
        edited_column: m.edited_column || m.original_column,
        has_uom: has,
        uom_options: options,
      };
    },
    seedFromGrid() {
      const cols = this.sourceGrid?.columns || [];
      if (!cols.length || this.mappings.length) return;
      this.mappings = cols.map((col) =>
        this.normalizeMappingRow({
          original_column: String(col),
          edited_column: String(col),
          mapped_column: "",
          mapped_uom: "",
          original_uom: "",
          has_uom: false,
          uom_options: [],
        }),
      );
    },
    applyPayload(data) {
      this.standardColumns = data.standard_columns || [];
      this.uomCatalog = data.uom_catalog || {};
      const rows = data.column_mappings || [];
      if (rows.length) {
        this.mappings = rows.map((m) => this.normalizeMappingRow({ ...m }));
      } else {
        this.seedFromGrid();
      }
    },
    async load() {
      const token = this.loadToken + 1;
      this.loadToken = token;
      this.seedFromGrid();
      if (!this.filename || this.tableIndex == null) return;
      if (!this.mappings.length) this.loading = true;
      try {
        const { data } = await ocrApi.get(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/columns`,
        );
        if (token !== this.loadToken) return;
        this.applyPayload(data);
      } catch (e) {
        if (token !== this.loadToken) return;
        this.$emit("status", { type: "error", message: formatOcrApiError(e, "Could not load column mappings.") });
      } finally {
        if (token === this.loadToken) this.loading = false;
      }
    },
    async runPredictColumns() {
      this.predicting = true;
      try {
        const { data } = await ocrApi.post(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/columns/predict`,
        );
        this.applyPayload(data);
        this.$emit("updated", data.table);
        this.$emit("status", { type: "success", message: "Column mapping predicted." });
      } catch (e) {
        this.$emit("status", { type: "error", message: formatOcrApiError(e, "Column predict failed.") });
      } finally {
        this.predicting = false;
      }
    },
    buildUpdates() {
      return this.mappings.map((m) => ({
        original_column: m.original_column,
        edited_column: m.edited_column,
        mapped_column: m.mapped_column,
        original_uom: m.has_uom ? (m.original_uom || "") : "",
        mapped_uom: m.has_uom ? m.mapped_uom : "",
      }));
    },
    async applyMappings() {
      this.applying = true;
      try {
        const { data } = await ocrApi.put(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/columns`,
          { mappings: this.buildUpdates() },
        );
        this.applyPayload(data);
        this.$emit("updated", data.table);
        this.$emit("status", { type: "success", message: "Column mapping applied." });
      } catch (e) {
        this.$emit("status", { type: "error", message: formatOcrApiError(e, "Could not apply column mapping.") });
      } finally {
        this.applying = false;
      }
    },
    async saveConfig() {
      this.savingConfig = true;
      try {
        const { data } = await ocrApi.post(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/columns/save-config`,
        );
        this.$emit("status", {
          type: data.ok ? "success" : "warning",
          message: data.message || (data.ok ? "Config saved." : "Save failed."),
        });
      } catch (e) {
        this.$emit("status", { type: "error", message: formatOcrApiError(e, "Could not save mapping config.") });
      } finally {
        this.savingConfig = false;
      }
    },
  },
};
</script>
